import { useEffect, useMemo, useRef, useState } from 'react'
import { INFRASTRUCTURE_ROLE_LABELS } from '../../../../types/case-container'

export type RolesInputTab = 'predefined' | 'custom'

const PREDEFINED_ROLE_KEYS = new Set(Object.keys(INFRASTRUCTURE_ROLE_LABELS))

interface UseRolesInputArgs {
  value: string[]
  onChange: (roles: string[]) => void
  existingCustomRoles: string[]
}

export interface RolesInputState {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  customRole: string
  setCustomRole: (value: string) => void
  activeTab: RolesInputTab
  setActiveTab: (tab: RolesInputTab) => void
  dropdownRef: React.RefObject<HTMLDivElement>
  customRolesInUse: string[]
  toggleRole: (role: string) => void
  addCustomRole: () => void
  handleCustomKeyDown: (event: React.KeyboardEvent) => void
}

export function useRolesInput({
  value,
  onChange,
  existingCustomRoles,
}: UseRolesInputArgs): RolesInputState {
  const [isOpen, setIsOpen] = useState(false)
  const [customRole, setCustomRole] = useState('')
  const [activeTab, setActiveTab] = useState<RolesInputTab>('predefined')
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const toggleRole = (role: string): void => {
    if (value.includes(role)) {
      onChange(value.filter((item) => item !== role))
    } else {
      onChange([...value, role])
    }
  }

  const addCustomRole = (): void => {
    const trimmed = customRole.trim().toLowerCase().replaceAll(/\s+/g, '_')
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed])
      setCustomRole('')
    }
  }

  const handleCustomKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault()
      addCustomRole()
    }
  }

  const customRolesInUse = useMemo(() => {
    return [
      ...new Set(existingCustomRoles.filter((role) => !PREDEFINED_ROLE_KEYS.has(role))),
    ].sort()
  }, [existingCustomRoles])

  return {
    isOpen,
    setIsOpen,
    customRole,
    setCustomRole,
    activeTab,
    setActiveTab,
    dropdownRef,
    customRolesInUse,
    toggleRole,
    addCustomRole,
    handleCustomKeyDown,
  }
}
